/**
 * OperatorHeader Component
 *
 * Compact sticky header with logo, notification bell (with unread badge),
 * and profile avatar with operator initials from Auth0.
 *
 * Uses bg-op-card/90 with backdrop-blur for the frosted glass effect.
 * "Guild Insights" text hidden on mobile (<=375px), visible on sm+.
 */

import { useAuth0 } from '@auth0/auth0-react';
import { Bell } from 'lucide-react';
import { useOperatorUnreadCount } from '../hooks/useOperatorNotifications';
import { cn } from '../../../lib/utils';
import logo from '../../../assets/images/logo.png';
import { getInitials } from '../utils';

interface OperatorHeaderProps {
  /** Additional CSS classes for the header */
  className?: string;
  /** Called when the avatar button is tapped (e.g., open profile sheet) */
  onAvatarClick?: () => void;
  /** Called when the bell button is tapped (e.g., open notifications) */
  onBellClick?: () => void;
}

export function OperatorHeader({ className, onAvatarClick, onBellClick }: OperatorHeaderProps) {
  const { user } = useAuth0();
  const { data: unreadData } = useOperatorUnreadCount();
  const unreadCount = unreadData?.count ?? 0;

  return (
    <header
      className={cn(
        'sticky top-0 z-50 h-14 bg-op-card/90 backdrop-blur-md',
        className,
      )}
    >
      <div className="mx-auto flex h-full max-w-lg items-center justify-between px-4">
        {/* Left: Logo */}
        <img src={logo} alt="Guild Insights" className="h-7 w-auto" />

        {/* Center: Brand name (hidden on small screens) */}
        <span className="hidden sm:block text-sm font-semibold text-op-text-primary">
          Guild Insights
        </span>

        {/* Right: Bell + Avatar */}
        <div className="flex items-center gap-1">
          {/* Notification bell */}
          <button
            type="button"
            onClick={onBellClick}
            className="relative flex h-11 w-11 items-center justify-center rounded-xl active:bg-gray-100 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
            aria-label={
              unreadCount > 0
                ? `Notifications, ${unreadCount} unread`
                : 'Notifications'
            }
          >
            <Bell size={20} strokeWidth={1.5} className="text-op-text-secondary" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-medium text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Profile avatar */}
          <button
            type="button"
            onClick={onAvatarClick}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-guild-green text-xs font-semibold text-white focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
            aria-label="Profile"
          >
            {getInitials(user?.name)}
          </button>
        </div>
      </div>
    </header>
  );
}
